/**
 * The cross-case review inbox.
 *
 * A reviewer does not think in cases; they think "what is waiting for me". Without this
 * page the human gate is reachable only by remembering which case had a pending merge,
 * and a gate people forget to open is a gate that gets rushed when they do.
 *
 * **It accounts for runs at the gate even though it cannot action them.** The dashboard
 * counts `runs_awaiting_review` and sends people here; this page used to compute what to
 * show from proposals and hypotheses alone, so a run that had been decided on and was
 * working through synthesis was counted as outstanding on one screen and absent from the
 * other. Both numbers were right and together they described a situation that did not
 * exist. A run stays at `awaiting_review` from the moment it reaches the gate until the
 * advance task moves it on, which spans both "blocked on you" and "already decided, still
 * finishing" — so the honest thing this page can do is say which it is.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { CaseResponse, Paged, TenantCounts } from '@/lib/schemas';
import { Card, EmptyState, ErrorState, Loading } from '@/components/ui';

/** The review inbox. */
export function ReviewInboxPage(): ReactElement {
  const counts = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => request<TenantCounts>('/dashboard'),
  });
  const cases = useQuery({
    queryKey: ['cases'],
    queryFn: () => request<Paged<CaseResponse>>('/cases', { query: { limit: 50 } }),
  });

  if (counts.isLoading || cases.isLoading) return <Loading />;
  if (counts.error) return <ErrorState error={counts.error} />;
  if (cases.error) return <ErrorState error={cases.error} />;

  const outstanding =
    (counts.data?.pending_proposals ?? 0) + (counts.data?.unconfirmed_hypotheses ?? 0);
  // Runs held at the gate. Not actionable from here — `awaiting_review` transitions only
  // to `synthesising`, `complete`, `cancelled` or `failed`, and nothing in the interface
  // performs any of them. Shown so the count on the dashboard has somewhere to land.
  const atGate = counts.data?.runs_awaiting_review ?? 0;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Review</h1>
      {outstanding === 0 ? (
        <EmptyState>
          <span data-testid="review-empty">Nothing is waiting on a decision.</span>
          {atGate > 0 && (
            // **Says which of the two states the run is in.** Without this the reader is
            // left to reconcile "1 run awaiting review" on the dashboard with an empty
            // page, and the reasonable conclusion — that something is broken — is wrong.
            <span className="mt-2 block" data-testid="review-runs-at-gate">
              {atGate} run{atGate === 1 ? '' : 's'} still finishing. A run sits at the
              review gate until its summary is written, which needs nobody.
            </span>
          )}
        </EmptyState>
      ) : (
        <Card title={`${outstanding} decisions waiting`}>
          <p className="mb-3 text-sm text-muted">
            {counts.data?.pending_proposals ?? 0} merges and{' '}
            {counts.data?.unconfirmed_hypotheses ?? 0} hypotheses. Nothing merges and nothing
            is exported until a person decides.
          </p>
          <ul className="space-y-1 text-sm">
            {(cases.data?.data ?? []).map((entry) => (
              <li key={entry.id}>
                <Link to={`/cases/${entry.id}/review`} className="text-accent">
                  {entry.reference} — {entry.title}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
